/**
 * Surety bond status as the client sees it on the portal — pure helpers.
 *
 * The CRM mirrors a client-safe `PortalProjectBond` onto the portal project
 * whenever a bond request is saved or its status changes, so the client can
 * follow bonding progress before the bond is issued. No firebase imports.
 */

import type { PortalProjectBond } from '../types';
import type { SuretyBondRequest } from '../types/regulatory';

type RequestLike = Pick<
  SuretyBondRequest,
  'id' | 'dealId' | 'status' | 'bondType' | 'bondAmount' | 'suretyName' | 'bondNumber' | 'letterDate' | 'issuedAt' | 'updatedAt'
>;

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Client-safe projection of a bond request. */
export function portalBondFromRequest(request: RequestLike, now: Date = new Date()): PortalProjectBond {
  const issued = request.status === 'issued';
  const bond: PortalProjectBond = {
    requestId: request.id,
    status: request.status,
    bondType: request.bondType,
    bondAmount: Math.round((Number(request.bondAmount) || 0) * 100) / 100,
    letterDate: request.letterDate,
    issuedAt: issued ? ymd(request.issuedAt ?? now) : null,
    updatedAt: now.toISOString(),
  };
  if (request.suretyName?.trim()) bond.suretyName = request.suretyName.trim();
  if (request.bondNumber?.trim()) bond.bondNumber = request.bondNumber.trim();
  return bond;
}

const STATUS_RANK: Record<SuretyBondRequest['status'], number> = {
  issued: 0,
  sent: 1,
  draft: 2,
  declined: 3,
};

/**
 * The one request worth showing the client when a deal has several (bid bond
 * then P&P, a declined attempt then a new one): issued beats sent beats draft
 * beats declined; ties go to the most recently updated.
 */
export function pickPortalBondRequest<T extends RequestLike>(requests: T[]): T | null {
  if (!requests.length) return null;
  return requests
    .slice()
    .sort((a, b) => {
      const rank = STATUS_RANK[a.status] - STATUS_RANK[b.status];
      if (rank !== 0) return rank;
      return (b.updatedAt?.getTime() ?? 0) - (a.updatedAt?.getTime() ?? 0);
    })[0];
}

export interface PortalBondCopy {
  /** Short pill text, e.g. "Bonded". */
  pill: string;
  /** Card heading. */
  title: string;
  /** One-sentence explanation for the client. */
  detail: string;
  tone: 'progress' | 'pending' | 'issued' | 'declined';
}

function bondTypeNoun(bondType: PortalProjectBond['bondType']): string {
  return bondType === 'bid' ? 'bid bond' : 'performance and payment bonds';
}

export function portalBondCopy(bond: PortalProjectBond): PortalBondCopy {
  const noun = bondTypeNoun(bond.bondType);
  switch (bond.status) {
    case 'issued': {
      const by = bond.suretyName ? ` by ${bond.suretyName}` : '';
      const num = bond.bondNumber ? ` (bond #${bond.bondNumber})` : '';
      return {
        pill: 'Bonded',
        title: bond.bondType === 'bid' ? 'Bid bond issued' : 'Project is bonded',
        detail: `The ${noun} for this project ${bond.bondType === 'bid' ? 'has' : 'have'} been issued${by}${num}.`,
        tone: 'issued',
      };
    }
    case 'sent':
      return {
        pill: 'Bond requested',
        title: 'Bond requested',
        detail: `We have asked our surety to issue the ${noun} for this project and are waiting on the surety.`,
        tone: 'pending',
      };
    case 'declined':
      return {
        pill: 'Bond pending',
        title: 'Bonding under review',
        detail: `The initial request for the ${noun} was not approved. Your project manager will follow up with next steps.`,
        tone: 'declined',
      };
    case 'draft':
    default:
      return {
        pill: 'Bond in progress',
        title: 'Bond request in preparation',
        detail: `We are preparing the request for the ${noun} on this project.`,
        tone: 'progress',
      };
  }
}

export function formatPortalBondAmount(amount: number): string {
  return (Number(amount) || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}
